import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Megaphone, Image as ImageIcon, FileText, Plus, Pencil, Trash2, X, Search, GripVertical,
  Eye, EyeOff, Calendar, Upload, AlertTriangle, Check, Building2, Clock, Sparkles,
} from 'lucide-react';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';

/* ───────────── Design : même palette que l’accueil ───────────── */
const C = {
  navy: '#0B1437', navy2: '#18246B', y: '#FFC93C', teal: '#25B8A8', coral: '#FF7A6B',
  vio: '#8B7CF6', sky: '#4EA8FF', ink: '#0B1437', mut: '#667091', line: '#E2E6F2', bg: '#F1F3FA',
};
const DISPLAY = "'Bricolage Grotesque','DM Sans',system-ui,sans-serif";
const BODY = "'DM Sans',system-ui,-apple-system,'Segoe UI',sans-serif";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;700;800&family=DM+Sans:wght@400;500;700&display=swap');
.cm-root { font-family: ${BODY}; color: ${C.ink}; }
.cm-display { font-family: ${DISPLAY}; letter-spacing: -.02em; }
.cm-focus:focus-visible { outline: 3px solid ${C.y}; outline-offset: 2px; }
.cm-input { width: 100%; padding: .6rem .85rem; border: 1px solid ${C.line}; border-radius: .75rem; background: #fff; font-size: .9rem; color: ${C.ink}; }
.cm-input:focus { outline: none; border-color: ${C.vio}; box-shadow: 0 0 0 3px ${C.vio}33; }
.cm-input.err { border-color: ${C.coral}; }
@keyframes cm-in { from { transform: translateX(40px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes cm-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes cm-up { from { transform: translateY(12px); opacity: 0; } to { transform: none; opacity: 1; } }
.cm-drawer { animation: cm-in .28s cubic-bezier(.2,.8,.2,1) both; }
.cm-backdrop { animation: cm-fade .2s ease both; }
.cm-toast { animation: cm-up .25s ease both; }
.cm-drop { outline: 2px dashed ${C.vio}; outline-offset: -6px; background: ${C.vio}0F; }
@media (prefers-reduced-motion: reduce) { .cm-drawer, .cm-backdrop, .cm-toast { animation: none; } }
`;

/* ───────────── Données & utilitaires ───────────── */
const CATS = ['Général', 'RH', 'Événement', 'Sécurité', 'Mot du Président'];
const PRESIDENT = 'Mot du Président';
const PRIORITES = [
  { id: 'normal', label: 'Normal', color: C.sky },
  { id: 'important', label: 'Important', color: C.y },
  { id: 'urgent', label: 'Urgent', color: C.coral },
];
const TEMPLATES = [
  { label: 'Événement', categorie: 'Événement', priorite: 'normal', contenu: '📅 Quand : \n📍 Où : \n👥 Pour qui : \n\nPour plus d’informations, rapprochez-vous de …' },
  { label: 'Information RH', categorie: 'RH', priorite: 'important', contenu: 'Chers collègues,\n\n…\n\nPour toute question, la Direction des Ressources Humaines reste à votre écoute.' },
  { label: 'Alerte', categorie: 'Sécurité', priorite: 'urgent', contenu: '⚠️ À lire attentivement : …\n\nMerci de votre vigilance.' },
  { label: 'Mot du Président', categorie: PRESIDENT, priorite: 'normal', titre: PRESIDENT, contenu: 'Chers collaborateurs,\n\n…\n\nBien cordialement,' },
];
const EMOJIS = ['📣', '🎉', '✅', '📅', '⚠️', '👋'];
const SIZES = { slide: { w: 1600, h: 700, label: '1600 × 700 px' }, vignette: { w: 600, h: 600, label: '600 × 600 px' } };

const imageUrl = (img) => {
  if (!img) return null;
  if (/^(https?:|data:|blob:)/.test(img)) return img;
  return `/api/communication/images/${img.split('/').pop()}`;
};
const imgOf = (it) => imageUrl(it?.image || it?.image_path);
const isOn = (v) => (v === undefined || v === null ? true : v === true || v === 1 || v === '1');
const day = (d) => (d ? String(d).slice(0, 10) : '');
const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const statusOf = (it) => {
  if (!isOn(it.active)) return 'inactive';
  if (it.date_expiration && day(it.date_expiration) < todayStr()) return 'expired';
  if (it.date_publication && day(it.date_publication) > todayStr()) return 'scheduled';
  return 'live';
};
const STATUS = {
  live: { label: 'En ligne', color: C.teal },
  scheduled: { label: 'Programmée', color: C.sky },
  expired: { label: 'Expirée', color: C.coral },
  inactive: { label: 'Brouillon', color: C.mut },
};
const prioColor = (p) => PRIORITES.find((x) => x.id === p)?.color || C.sky;

const blank = (mode, filialeId) => ({
  titre: '', description: '', contenu: '',
  categorie: mode === 'creations' ? 'Communication' : 'Général',
  type: 'slide', priorite: 'normal', ordre: 0,
  active: true, date_publication: todayStr(), date_expiration: '', filiale_id: filialeId || '',
});
const fromItem = (it) => ({
  titre: it.titre || '', description: it.description || '', contenu: it.contenu || '',
  categorie: it.categorie || 'Général', type: it.type || 'slide', priorite: it.priorite || 'normal',
  ordre: it.ordre ?? 0, active: isOn(it.active), date_publication: day(it.date_publication),
  date_expiration: day(it.date_expiration), filiale_id: it.filiale_id ?? '',
});
const toFormData = (mode, f, file, auteur) => {
  const fd = new FormData();
  fd.append('titre', f.titre.trim());
  fd.append('categorie', f.categorie.trim() || (mode === 'creations' ? 'Communication' : 'Général'));
  fd.append('active', f.active ? 1 : 0);
  fd.append('date_publication', f.date_publication || '');
  fd.append('filiale_id', f.filiale_id || '');
  if (auteur) fd.append('auteur', auteur);
  if (mode === 'creations') {
    fd.append('description', f.description);
    fd.append('type', f.type);
    fd.append('ordre', f.ordre || 0);
  } else {
    fd.append('contenu', f.contenu);
    fd.append('priorite', f.priorite);
    fd.append('date_expiration', f.date_expiration || '');
  }
  if (file) fd.append('image', file);
  return fd;
};
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

/* ───────────── Petits composants ───────────── */
const StatusChip = ({ status }) => {
  const s = STATUS[status];
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-full" style={{ background: `${s.color}1F`, color: status === 'inactive' ? C.mut : s.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
      {s.label}
    </span>
  );
};

const Switch = ({ checked, onChange, label }) => (
  <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}
    className="relative flex-none w-10 h-6 transition-colors rounded-full cm-focus" style={{ background: checked ? C.teal : '#C9CFE3' }}>
    <span className="absolute w-4 h-4 transition-all bg-white rounded-full top-1" style={{ left: checked ? 22 : 4 }} />
  </button>
);

const Field = ({ label, hint, error, children, right }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="text-sm font-bold">{label}</label>
      {right && <span className="text-xs" style={{ color: C.mut }}>{right}</span>}
    </div>
    {children}
    {error ? <p className="mt-1.5 text-xs font-medium" style={{ color: '#C0392B' }}>{error}</p>
      : hint ? <p className="mt-1.5 text-xs" style={{ color: C.mut }}>{hint}</p> : null}
  </div>
);

const Chip = ({ active, onClick, children, color }) => (
  <button type="button" onClick={onClick} aria-pressed={active}
    className="px-3 py-1.5 text-sm font-medium transition-colors border rounded-full cm-focus"
    style={{ background: active ? C.navy : '#fff', color: active ? '#fff' : C.ink, borderColor: active ? C.navy : C.line }}>
    {color && <span className="inline-block w-2 h-2 mr-2 rounded-full" style={{ background: color }} />}
    {children}
  </button>
);

/* ───────────── Aperçus (reproduisent l’accueil) ───────────── */
const PreviewAnnonce = ({ f, auteur }) => {
  if (f.categorie === PRESIDENT) {
    return (
      <section className="flex items-center gap-4 px-6 py-5 bg-white rounded-2xl" style={{ border: `1px solid ${C.line}`, borderLeft: `6px solid ${C.navy}` }}>
        <Megaphone className="flex-none w-6 h-6" style={{ color: C.navy }} />
        <div className="min-w-0">
          <div className="text-xs font-bold" style={{ color: C.mut }}>Le mot du Président</div>
          <p className="mt-1 text-lg cm-display" style={{ fontWeight: 500 }}>{f.contenu || 'Votre message apparaîtra ici.'}</p>
        </div>
      </section>
    );
  }
  const color = prioColor(f.priorite);
  return (
    <article className="relative overflow-hidden bg-white rounded-2xl" style={{ border: `1px solid ${C.line}` }}>
      <div className="absolute top-0 left-0 w-1 h-full" style={{ background: color }} />
      <div className="p-5 pl-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <span className="inline-block px-2 py-1 mb-2 text-xs font-bold rounded-md" style={{ background: `${color}20`, color: f.priorite === 'important' ? '#8A6100' : color }}>
              {f.categorie || 'Annonce'}
            </span>
            <h4 className="text-base leading-tight cm-display" style={{ fontWeight: 700 }}>{f.titre || 'Titre de votre annonce'}</h4>
          </div>
          {f.priorite === 'urgent' && <span className="flex-none w-2 h-2 rounded-full" style={{ background: C.coral }} />}
        </div>
        <p className="text-sm leading-relaxed whitespace-pre-line line-clamp-3" style={{ color: C.mut }}>
          {f.contenu || 'Le texte de votre annonce apparaîtra ici, tel que vos collègues le verront.'}
        </p>
        <div className="flex items-center justify-between pt-3 mt-4 text-xs border-t" style={{ borderColor: C.line, color: C.mut }}>
          <span className="flex items-center gap-2"><Calendar className="w-3 h-3" />{fmtDate(f.date_publication || todayStr())}</span>
          {auteur && <span className="font-medium" style={{ color: C.vio }}>{auteur}</span>}
        </div>
      </div>
    </article>
  );
};

const PreviewCreation = ({ f, src }) => {
  const slide = f.type === 'slide';
  return (
    <div className="relative mx-auto overflow-hidden text-white rounded-2xl"
      style={{ aspectRatio: slide ? '16 / 7' : '1 / 1', maxWidth: slide ? '100%' : 260, background: `linear-gradient(135deg, ${C.navy2}, ${C.vio})` }}>
      {src && <img src={src} alt="" className="absolute inset-0 object-cover w-full h-full" />}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(11,20,55,0) 45%, rgba(11,20,55,.85))' }} />
      <div className="absolute inset-x-0 bottom-0 p-4">
        {slide && <span className="inline-block px-3 py-1 mb-2 text-xs font-bold rounded-full" style={{ background: C.y, color: C.navy }}>{f.categorie || 'Communication'}</span>}
        <div className={`${slide ? 'text-xl' : 'text-xs'} leading-snug truncate cm-display`} style={{ fontWeight: 800 }}>{f.titre || 'Titre de la création'}</div>
        {slide && f.description && <p className="mt-0.5 text-sm truncate" style={{ color: '#D5DBF7' }}>{f.description}</p>}
      </div>
    </div>
  );
};

/* ───────────── Volet d’édition ───────────── */
const TABS = [{ id: 'contenu', label: 'Contenu' }, { id: 'visuel', label: 'Visuel' }, { id: 'diffusion', label: 'Diffusion' }];

const Editor = ({ mode, item, preset, onClose, onSaved, notify }) => {
  const { user } = useAuth();
  const { currentFiliale } = useFiliale();
  const isAnnonce = mode === 'annonces';
  const auteur = item?.auteur || [user?.prenom, user?.nom].filter(Boolean).join(' ');

  const initial = useMemo(() => (item ? fromItem(item) : { ...blank(mode), ...(preset || {}) }), []); // eslint-disable-line
  const [f, setF] = useState(initial);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(item ? imgOf(item) : null);
  const [ratioWarn, setRatioWarn] = useState('');
  const [tab, setTab] = useState('contenu');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const areaRef = useRef(null);

  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const dirty = JSON.stringify(f) !== JSON.stringify(initial) || !!file;
  const requestClose = () => (dirty ? setConfirmClose(true) : onClose());

  useEffect(() => () => { if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview); }, [preview]);

  const pickFile = (fl) => {
    if (!fl) return;
    if (!fl.type.startsWith('image/')) { notify('Ce fichier n’est pas une image. Choisissez un PNG, JPG ou WebP.', 'error'); return; }
    if (fl.size > 5 * 1024 * 1024) { notify('Image trop lourde (5 Mo maximum). Compressez-la puis réessayez.', 'error'); return; }
    const url = URL.createObjectURL(fl);
    const probe = new window.Image();
    probe.onload = () => {
      const target = mode === 'creations' ? SIZES[f.type] : null;
      if (target) {
        const diff = Math.abs(probe.naturalWidth / probe.naturalHeight - target.w / target.h) / (target.w / target.h);
        setRatioWarn(diff > 0.15 ? `Format ${probe.naturalWidth} × ${probe.naturalHeight} : l’image sera recadrée. Idéal : ${target.label}.` : '');
      }
    };
    probe.src = url;
    setFile(fl); setPreview(url); setErrors((e) => ({ ...e, image: undefined }));
  };

  const validate = (v) => {
    const e = {};
    if (!v.titre.trim()) e.titre = 'Donnez un titre à votre ' + (isAnnonce ? 'annonce.' : 'création.');
    if (isAnnonce && !v.contenu.trim()) e.contenu = 'Écrivez le message à diffuser.';
    if (!isAnnonce && !file && !item) e.image = 'Ajoutez le visuel de cette création.';
    return e;
  };

  const submit = async (publish) => {
    const next = { ...f, active: publish };
    const errs = validate(next);
    setErrors(errs);
    if (Object.keys(errs).length) { setTab(errs.titre || errs.contenu ? 'contenu' : 'visuel'); return; }
    setSaving(publish ? 'publish' : 'draft');
    try {
      const fd = toFormData(mode, next, file, auteur);
      if (item) await http.put(`/api/communication/${mode}/${item.id}`, fd, MULTIPART);
      else await http.post(`/api/communication/${mode}`, fd, MULTIPART);
      onSaved(!publish ? 'Brouillon enregistré' : item && isOn(item.active) ? 'Enregistré' : 'Publié');
    } catch (err) {
      notify(err?.response?.data?.error || 'Enregistrement impossible. Vérifiez votre connexion puis réessayez.', 'error');
    } finally { setSaving(null); }
  };

  const latest = useRef({});
  latest.current = { submit, requestClose, f };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') latest.current.requestClose();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); latest.current.submit(latest.current.f.active); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const insert = (txt) => {
    const el = areaRef.current;
    if (!el) { set('contenu', f.contenu + txt); return; }
    const s = el.selectionStart ?? f.contenu.length;
    const e = el.selectionEnd ?? s;
    set('contenu', f.contenu.slice(0, s) + txt + f.contenu.slice(e));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + txt.length, s + txt.length); });
  };

  const tabHasError = (id) => (id === 'contenu' && (errors.titre || errors.contenu)) || (id === 'visuel' && errors.image);
  const showTemplates = isAnnonce && !item && !f.titre && !f.contenu;
  const primaryLabel = item && isOn(item.active) ? 'Enregistrer' : 'Publier';
  const filialeLabel = currentFiliale?.libelle || 'Filiale courante';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 cm-backdrop" style={{ background: 'rgba(11,20,55,.55)' }} onClick={requestClose} />
      <aside role="dialog" aria-modal="true" aria-label={isAnnonce ? 'Éditeur d’annonce' : 'Éditeur de création'}
        className="relative flex flex-col w-full h-full bg-white shadow-2xl cm-drawer" style={{ maxWidth: 1040 }}>

        {/* En-tête */}
        <header className="flex items-center gap-3 px-6 py-4 border-b" style={{ borderColor: C.line }}>
          <div className="flex items-center justify-center w-10 h-10 rounded-xl" style={{ background: C.navy, color: C.y }}>
            {isAnnonce ? <FileText className="w-5 h-5" /> : <ImageIcon className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <h2 className="text-lg truncate cm-display" style={{ fontWeight: 800 }}>
              {item ? (isAnnonce ? 'Modifier l’annonce' : 'Modifier la création') : (isAnnonce ? 'Nouvelle annonce' : 'Nouvelle création')}
            </h2>
            <p className="text-xs" style={{ color: C.mut }}>{dirty ? 'Modifications non enregistrées' : 'Tout est à jour'} · Ctrl+S pour enregistrer</p>
          </div>
          <button type="button" onClick={requestClose} aria-label="Fermer l’éditeur" className="p-2 ml-auto rounded-lg cm-focus hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </header>

        <div className="flex flex-1 min-h-0">
          {/* Formulaire */}
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex gap-1 px-6 pt-3 border-b" style={{ borderColor: C.line }} role="tablist">
              {TABS.map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
                  className="relative px-4 py-2.5 text-sm font-bold cm-focus"
                  style={{ color: tab === t.id ? C.navy : C.mut, borderBottom: `3px solid ${tab === t.id ? C.y : 'transparent'}`, marginBottom: -1 }}>
                  {t.label}
                  {tabHasError(t.id) && <span className="absolute w-2 h-2 rounded-full top-2 right-1.5" style={{ background: C.coral }} />}
                </button>
              ))}
            </div>

            <div className="flex-1 px-6 py-5 space-y-5 overflow-y-auto">
              {tab === 'contenu' && (
                <>
                  {showTemplates && (
                    <div className="p-4 rounded-2xl" style={{ background: C.bg }}>
                      <div className="flex items-center gap-2 mb-2 text-sm font-bold"><Sparkles className="w-4 h-4" style={{ color: C.vio }} />Partir d’un modèle</div>
                      <div className="flex flex-wrap gap-2">
                        {TEMPLATES.map((t) => (
                          <button key={t.label} type="button" onClick={() => { setF((p) => ({ ...p, ...t, titre: t.titre ?? p.titre })); notify(`Modèle « ${t.label} » appliqué`); }}
                            className="px-3 py-1.5 text-sm font-medium bg-white border rounded-full cm-focus hover:shadow-sm" style={{ borderColor: C.line }}>{t.label}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  <Field label="Titre" error={errors.titre} right={`${f.titre.length}/80`}>
                    <input className={`cm-input ${errors.titre ? 'err' : ''}`} maxLength={80} value={f.titre} onChange={(e) => set('titre', e.target.value)}
                      placeholder={isAnnonce ? 'Ex. Journée portes ouvertes du 15 novembre' : 'Ex. Octobre rose'} autoFocus />
                  </Field>

                  {isAnnonce ? (
                    <>
                      <Field label="Message" error={errors.contenu} right={`${f.contenu.length} caractères`}
                        hint="La carte de l’accueil affiche environ 3 lignes : allez à l’essentiel.">
                        <div className="overflow-hidden border rounded-xl" style={{ borderColor: errors.contenu ? C.coral : C.line }}>
                          <div className="flex items-center gap-1 px-2 py-1.5 border-b" style={{ background: C.bg, borderColor: C.line }}>
                            <button type="button" onClick={() => insert('\n• ')} className="px-2 py-1 text-sm font-bold rounded-md cm-focus hover:bg-white" title="Ajouter une puce">• Puce</button>
                            <span className="w-px h-5 mx-1" style={{ background: C.line }} />
                            {EMOJIS.map((em) => (
                              <button key={em} type="button" onClick={() => insert(em + ' ')} className="w-8 h-8 text-base rounded-md cm-focus hover:bg-white" aria-label={`Insérer ${em}`}>{em}</button>
                            ))}
                          </div>
                          <textarea ref={areaRef} rows={9} value={f.contenu} onChange={(e) => set('contenu', e.target.value)}
                            className="w-full p-3 text-sm leading-relaxed resize-y focus:outline-none" placeholder="Écrivez votre message…" />
                        </div>
                      </Field>

                      <Field label="Catégorie">
                        <div className="flex flex-wrap gap-2">
                          {CATS.map((c) => (
                            <Chip key={c} active={f.categorie === c} onClick={() => set('categorie', c)}>{c}</Chip>
                          ))}
                        </div>
                        <input className="mt-2 cm-input" value={CATS.includes(f.categorie) ? '' : f.categorie} onChange={(e) => set('categorie', e.target.value)} placeholder="Ou saisissez une autre catégorie" />
                      </Field>

                      <Field label="Priorité" hint="Une annonce urgente est signalée par un point rouge sur l’accueil.">
                        <div className="flex flex-wrap gap-2">
                          {PRIORITES.map((p) => <Chip key={p.id} color={p.color} active={f.priorite === p.id} onClick={() => set('priorite', p.id)}>{p.label}</Chip>)}
                        </div>
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label="Description" hint="Affichée sous le titre sur les grandes slides.">
                        <textarea rows={3} className="cm-input" value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="Une phrase pour donner le contexte" />
                      </Field>
                      <Field label="Format">
                        <div className="grid grid-cols-2 gap-3">
                          {[{ id: 'slide', t: 'Slide', d: 'Grande image du carrousel' }, { id: 'vignette', t: 'Vignette', d: 'Petit carré à droite' }].map((o) => (
                            <button key={o.id} type="button" onClick={() => set('type', o.id)} aria-pressed={f.type === o.id}
                              className="flex items-center gap-3 p-3 text-left border-2 rounded-xl cm-focus"
                              style={{ borderColor: f.type === o.id ? C.vio : C.line, background: f.type === o.id ? `${C.vio}0D` : '#fff' }}>
                              <span className="flex-none rounded-md" style={{ width: o.id === 'slide' ? 44 : 24, height: 24, background: `linear-gradient(135deg, ${C.navy2}, ${C.vio})` }} />
                              <span><span className="block text-sm font-bold">{o.t}</span><span className="block text-xs" style={{ color: C.mut }}>{o.d}</span></span>
                            </button>
                          ))}
                        </div>
                      </Field>
                      <Field label="Catégorie">
                        <input className="cm-input" value={f.categorie} onChange={(e) => set('categorie', e.target.value)} />
                      </Field>
                    </>
                  )}
                </>
              )}

              {tab === 'visuel' && (
                <Field label={isAnnonce ? 'Image (facultative)' : 'Visuel'} error={errors.image}
                  hint={!isAnnonce ? `Format idéal : ${SIZES[f.type].label} · PNG, JPG ou WebP · 5 Mo maximum` : 'PNG, JPG ou WebP · 5 Mo maximum'}>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
                    className={`relative overflow-hidden border rounded-2xl ${dragOver ? 'cm-drop' : ''}`} style={{ borderColor: errors.image ? C.coral : C.line, background: C.bg }}>
                    {preview ? (
                      <img src={preview} alt="Aperçu du visuel" className="object-cover w-full" style={{ maxHeight: 280 }} />
                    ) : (
                      <button type="button" onClick={() => fileRef.current?.click()} className="flex flex-col items-center justify-center w-full gap-2 px-6 py-14 cm-focus">
                        <Upload className="w-7 h-7" style={{ color: C.vio }} />
                        <span className="text-sm font-bold">Glissez une image ici</span>
                        <span className="text-xs" style={{ color: C.mut }}>ou cliquez pour parcourir vos fichiers</span>
                      </button>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
                  {preview && (
                    <div className="flex gap-2 mt-3">
                      <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-1.5 text-sm font-medium bg-white border rounded-lg cm-focus" style={{ borderColor: C.line }}>Remplacer l’image</button>
                      {file && <button type="button" onClick={() => { setFile(null); setPreview(item ? imgOf(item) : null); setRatioWarn(''); }} className="px-3 py-1.5 text-sm font-medium rounded-lg cm-focus" style={{ color: C.mut }}>Annuler le changement</button>}
                    </div>
                  )}
                  {ratioWarn && (
                    <p className="flex items-start gap-2 p-3 mt-3 text-xs rounded-xl" style={{ background: '#FFF6DB', color: '#7A5A00' }}>
                      <AlertTriangle className="flex-none w-4 h-4" />{ratioWarn}
                    </p>
                  )}
                </Field>
              )}

              {tab === 'diffusion' && (
                <>
                  <div className="flex items-center gap-4 p-4 border rounded-2xl" style={{ borderColor: C.line }}>
                    <div className="flex-1">
                      <div className="text-sm font-bold">Visible sur l’accueil</div>
                      <div className="text-xs" style={{ color: C.mut }}>Désactivé, l’élément reste en brouillon.</div>
                    </div>
                    <Switch checked={f.active} onChange={(v) => set('active', v)} label="Visible sur l’accueil" />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Publication">
                      <input type="date" className="cm-input" value={f.date_publication} onChange={(e) => set('date_publication', e.target.value)} />
                    </Field>
                    {isAnnonce && (
                      <Field label="Expiration" hint={f.date_expiration ? '' : 'Sans date, l’annonce reste en ligne.'}>
                        <input type="date" className="cm-input" value={f.date_expiration} min={f.date_publication || undefined} onChange={(e) => set('date_expiration', e.target.value)} />
                        <div className="flex flex-wrap gap-2 mt-2">
                          {[{ l: '7 jours', n: 7 }, { l: '30 jours', n: 30 }].map((q) => (
                            <button key={q.n} type="button" onClick={() => set('date_expiration', addDays(q.n))} className="px-2.5 py-1 text-xs font-medium border rounded-full cm-focus" style={{ borderColor: C.line }}>+ {q.l}</button>
                          ))}
                          <button type="button" onClick={() => set('date_expiration', '')} className="px-2.5 py-1 text-xs font-medium border rounded-full cm-focus" style={{ borderColor: C.line }}>Sans fin</button>
                        </div>
                      </Field>
                    )}
                    {!isAnnonce && (
                      <Field label="Position" hint="Les 3 premières passent dans le carrousel, les 2 suivantes en vignettes. Réordonnez plutôt par glisser-déposer dans la liste.">
                        <input type="number" min={0} className="cm-input" value={f.ordre} onChange={(e) => set('ordre', parseInt(e.target.value, 10) || 0)} />
                      </Field>
                    )}
                  </div>

                  <Field label="Audience">
                    <div className="grid grid-cols-2 gap-3">
                      {[{ v: '', t: 'Toutes les filiales', d: 'Visible par tout le groupe' }, { v: currentFiliale?.id ?? '', t: filialeLabel, d: 'Uniquement cette filiale', off: !currentFiliale?.id }].map((o) => (
                        <button key={o.t} type="button" disabled={o.off} onClick={() => set('filiale_id', o.v)} aria-pressed={String(f.filiale_id) === String(o.v)}
                          className="flex items-center gap-3 p-3 text-left border-2 rounded-xl cm-focus disabled:opacity-40"
                          style={{ borderColor: String(f.filiale_id) === String(o.v) ? C.vio : C.line, background: String(f.filiale_id) === String(o.v) ? `${C.vio}0D` : '#fff' }}>
                          <Building2 className="flex-none w-5 h-5" style={{ color: C.vio }} />
                          <span className="min-w-0"><span className="block text-sm font-bold truncate">{o.t}</span><span className="block text-xs" style={{ color: C.mut }}>{o.d}</span></span>
                        </button>
                      ))}
                    </div>
                  </Field>
                </>
              )}
            </div>
          </div>

          {/* Aperçu en direct */}
          <div className="flex-col hidden p-6 overflow-y-auto border-l lg:flex" style={{ width: 400, background: C.bg, borderColor: C.line }}>
            <div className="flex items-center gap-2 mb-1 text-sm font-bold"><Eye className="w-4 h-4" style={{ color: C.vio }} />Aperçu sur l’accueil</div>
            <p className="mb-4 text-xs" style={{ color: C.mut }}>Se met à jour pendant que vous écrivez.</p>
            {isAnnonce ? <PreviewAnnonce f={f} auteur={auteur} /> : <PreviewCreation f={f} src={preview} />}
            {!f.active && <p className="mt-4 text-xs" style={{ color: C.mut }}>En brouillon : cet élément n’est pas visible par vos collègues.</p>}
            {isAnnonce && f.date_expiration && <p className="flex items-center gap-1.5 mt-4 text-xs" style={{ color: C.mut }}><Clock className="w-3.5 h-3.5" />Retirée automatiquement le {fmtDate(f.date_expiration)}</p>}
          </div>
        </div>

        {/* Pied */}
        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: C.line }}>
          <button type="button" onClick={requestClose} className="px-4 py-2.5 text-sm font-medium rounded-xl cm-focus hover:bg-gray-100">Annuler</button>
          <button type="button" disabled={!!saving} onClick={() => submit(false)} className="px-4 py-2.5 text-sm font-bold bg-white border rounded-xl cm-focus disabled:opacity-50" style={{ borderColor: C.line }}>
            {saving === 'draft' ? 'Enregistrement…' : 'Enregistrer en brouillon'}
          </button>
          <button type="button" disabled={!!saving} onClick={() => submit(true)} className="px-5 py-2.5 text-sm font-bold rounded-xl cm-focus disabled:opacity-50" style={{ background: C.y, color: C.navy }}>
            {saving === 'publish' ? 'Publication…' : primaryLabel}
          </button>
        </footer>

        {confirmClose && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-6" style={{ background: 'rgba(11,20,55,.45)' }}>
            <div className="w-full max-w-sm p-6 bg-white shadow-2xl rounded-2xl" role="alertdialog" aria-label="Quitter sans enregistrer">
              <h3 className="text-lg cm-display" style={{ fontWeight: 800 }}>Quitter sans enregistrer ?</h3>
              <p className="mt-2 text-sm" style={{ color: C.mut }}>Vos modifications seront perdues.</p>
              <div className="flex justify-end gap-2 mt-5">
                <button type="button" onClick={() => setConfirmClose(false)} className="px-4 py-2 text-sm font-bold border rounded-xl cm-focus" style={{ borderColor: C.line }}>Continuer l’édition</button>
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-bold text-white rounded-xl cm-focus" style={{ background: C.coral }}>Quitter</button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
};

/* ───────────── Page ───────────── */
const Communication = () => {
  const [tab, setTab] = useState('creations');
  const [creations, setCreations] = useState([]);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [editor, setEditor] = useState(null); // { mode, item, preset }
  const [del, setDel] = useState(null);
  const [toast, setToast] = useState(null);
  const dragFrom = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const notify = useCallback((msg, kind = 'ok') => {
    setToast({ msg, kind, id: Date.now() });
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchData = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([http.get('/api/communication/creations'), http.get('/api/communication/annonces')]);
      const cl = Array.isArray(c.data) ? c.data : c.data?.data || [];
      setCreations([...cl].sort((x, y) => (x.ordre ?? 0) - (y.ordre ?? 0)));
      setAnnonces(Array.isArray(a.data) ? a.data : a.data?.data || []);
    } catch (e) {
      notify('Chargement impossible. Actualisez la page.', 'error');
    } finally { setLoading(false); }
  }, [notify]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const mode = tab;
  const items = mode === 'creations' ? creations : annonces;
  const counts = useMemo(() => {
    const c = { all: items.length, live: 0, scheduled: 0, expired: 0, inactive: 0 };
    items.forEach((i) => { c[statusOf(i)] += 1; });
    return c;
  }, [items]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => (filter === 'all' || statusOf(i) === filter)
      && (!q || [i.titre, i.description, i.contenu, i.categorie].some((s) => (s || '').toLowerCase().includes(q))));
  }, [items, query, filter]);
  const president = annonces.find((a) => a.categorie === PRESIDENT);
  const canReorder = mode === 'creations' && !query && filter === 'all';

  const switchTab = (t) => { setTab(t); setFilter('all'); setQuery(''); };
  const open = (m, item = null, preset = null) => setEditor({ mode: m, item, preset });
  const onSaved = (msg) => { setEditor(null); notify(msg); fetchData(); };

  const toggleActive = async (it) => {
    try {
      await http.put(`/api/communication/${mode}/${it.id}`, toFormData(mode, { ...fromItem(it), active: !isOn(it.active) }, null, it.auteur), MULTIPART);
      notify(isOn(it.active) ? 'Masqué de l’accueil' : 'Publié');
      fetchData();
    } catch { notify('Action impossible. Réessayez dans un instant.', 'error'); }
  };
  const confirmDelete = async () => {
    const { m, item } = del;
    try { await http.delete(`/api/communication/${m}/${item.id}`); notify('Supprimé'); setDel(null); fetchData(); }
    catch { notify('Suppression impossible. Réessayez dans un instant.', 'error'); setDel(null); }
  };
  const reorder = async (from, to) => {
    if (from === null || from === to) return;
    const prev = creations;
    const next = [...creations];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setCreations(next);
    try { await http.put('/api/communication/creations/reorder', { items: next.map((it, i) => ({ id: it.id, ordre: i })) }); notify('Ordre enregistré'); }
    catch { setCreations(prev); notify('Réordonnancement impossible.', 'error'); }
  };

  const position = (i) => (i < 3 ? { l: 'Carrousel', c: C.vio } : i < 5 ? { l: 'Vignette', c: C.teal } : { l: 'Hors accueil', c: C.mut });
  const FILTERS = [['all', 'Tout'], ['live', 'En ligne'], ['scheduled', 'Programmées'], ['expired', 'Expirées'], ['inactive', 'Brouillons']];

  return (
    <div className="cm-root" style={{ background: C.bg, minHeight: '100vh' }}>
      <style>{CSS}</style>
      <BlueHeader title="Gestion de la Communication" subtitle="Composez ce que vos collègues voient en arrivant sur l’accueil" icon={Megaphone} showBack={false} />

      <div className="px-6 py-6">
        {/* Barre d’outils */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="inline-flex p-1 bg-white border rounded-xl" style={{ borderColor: C.line }} role="tablist">
            {[['creations', 'Créations visuelles', ImageIcon, creations.length], ['annonces', 'Annonces', FileText, annonces.length]].map(([id, label, Icon, n]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => switchTab(id)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg cm-focus"
                style={{ background: tab === id ? C.navy : 'transparent', color: tab === id ? '#fff' : C.mut }}>
                <Icon className="w-4 h-4" />{label}
                <span className="px-1.5 text-xs rounded-md" style={{ background: tab === id ? 'rgba(255,255,255,.18)' : C.bg }}>{n}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2" style={{ color: C.mut }} />
            <input className="cm-input" style={{ paddingLeft: '2.25rem' }} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un titre, une catégorie…" aria-label="Rechercher" />
          </div>
          <button type="button" onClick={() => open(mode)} className="flex items-center gap-2 px-4 py-2.5 ml-auto text-sm font-bold rounded-xl cm-focus" style={{ background: C.y, color: C.navy }}>
            <Plus className="w-4 h-4" />{mode === 'creations' ? 'Nouvelle création' : 'Nouvelle annonce'}
          </button>
        </div>

        {/* Mot du Président */}
        {mode === 'annonces' && !loading && (
          <section className="flex items-center gap-4 p-5 mb-4 text-white shadow-sm rounded-2xl" style={{ background: `linear-gradient(120deg, ${C.navy}, ${C.navy2})` }}>
            <div className="flex items-center justify-center flex-none w-12 h-12 rounded-xl" style={{ background: C.y, color: C.navy }}><Megaphone className="w-6 h-6" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-base cm-display" style={{ fontWeight: 800 }}>Le mot du Président</div>
              <p className="text-sm truncate" style={{ color: '#D5DBF7' }}>
                {president ? president.contenu : 'Aucun message pour le moment. Il s’affiche en bas de l’accueil, à la place du mot du jour.'}
              </p>
            </div>
            {president && <StatusChip status={statusOf(president)} />}
            <button type="button" onClick={() => (president ? open('annonces', president) : open('annonces', null, { categorie: PRESIDENT, titre: PRESIDENT }))}
              className="px-4 py-2 text-sm font-bold rounded-xl cm-focus" style={{ background: '#fff', color: C.navy }}>
              {president ? 'Modifier' : 'Rédiger'}
            </button>
          </section>
        )}

        {/* Filtres */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {FILTERS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setFilter(id)} aria-pressed={filter === id}
              className="px-3 py-1.5 text-sm font-medium border rounded-full cm-focus"
              style={{ background: filter === id ? C.navy : '#fff', color: filter === id ? '#fff' : C.ink, borderColor: filter === id ? C.navy : C.line }}>
              {label} <span style={{ opacity: .6 }}>{counts[id]}</span>
            </button>
          ))}
          {mode === 'creations' && (
            <span className="ml-auto text-xs" style={{ color: C.mut }}>
              {canReorder ? 'Glissez les cartes pour changer l’ordre d’affichage.' : 'Retirez les filtres pour réordonner.'}
            </span>
          )}
        </div>

        {/* Contenu */}
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-56 bg-white animate-pulse rounded-2xl" style={{ border: `1px solid ${C.line}` }} />)}
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center px-6 py-16 text-center bg-white rounded-2xl" style={{ border: `1px dashed ${C.line}` }}>
            <div className="flex items-end gap-2 mb-4" aria-hidden="true">
              {[C.sky, C.y, C.teal, C.vio].map((c, i) => <span key={c} className="rounded-full" style={{ width: 12 + i * 4, height: 12 + i * 4, background: c }} />)}
            </div>
            <p className="text-lg cm-display" style={{ fontWeight: 700 }}>{items.length === 0 ? (mode === 'creations' ? 'Aucune création pour le moment' : 'Aucune annonce pour le moment') : 'Aucun résultat'}</p>
            <p className="max-w-sm mt-1 text-sm" style={{ color: C.mut }}>
              {items.length === 0 ? 'Votre première publication apparaîtra sur l’accueil dès qu’elle sera en ligne.' : 'Essayez un autre mot-clé ou retirez un filtre.'}
            </p>
            {items.length === 0 && (
              <button type="button" onClick={() => open(mode)} className="flex items-center gap-2 px-4 py-2.5 mt-5 text-sm font-bold rounded-xl cm-focus" style={{ background: C.y, color: C.navy }}>
                <Plus className="w-4 h-4" />{mode === 'creations' ? 'Ajouter une création' : 'Écrire une annonce'}
              </button>
            )}
          </div>
        ) : mode === 'creations' ? (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {visible.map((it, i) => {
              const pos = position(creations.indexOf(it));
              const st = statusOf(it);
              return (
                <article key={it.id} draggable={canReorder}
                  onDragStart={() => { dragFrom.current = creations.indexOf(it); }}
                  onDragOver={(e) => { if (canReorder) { e.preventDefault(); setDragOver(it.id); } }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={() => { setDragOver(null); reorder(dragFrom.current, creations.indexOf(it)); dragFrom.current = null; }}
                  className="overflow-hidden bg-white rounded-2xl group"
                  style={{ border: `1px solid ${dragOver === it.id ? C.vio : C.line}`, boxShadow: dragOver === it.id ? `0 0 0 3px ${C.vio}33` : 'none', opacity: st === 'inactive' ? .75 : 1 }}>
                  <div className="relative" style={{ aspectRatio: it.type === 'vignette' ? '1 / 1' : '16 / 7', background: `linear-gradient(135deg, ${C.navy2}, ${C.vio})` }}>
                    {imgOf(it) && <img src={imgOf(it)} alt={it.titre} className="object-cover w-full h-full" />}
                    <span className="absolute px-2.5 py-1 text-xs font-bold rounded-full top-3 left-3" style={{ background: '#fff', color: pos.c }}>{pos.l}</span>
                    {canReorder && <span className="absolute p-1 rounded-md top-3 right-3 cursor-grab" style={{ background: 'rgba(11,20,55,.6)', color: '#fff' }} aria-hidden="true"><GripVertical className="w-4 h-4" /></span>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1.5"><StatusChip status={st} /><span className="text-xs" style={{ color: C.mut }}>{it.type === 'vignette' ? 'Vignette' : 'Slide'}</span></div>
                    <h3 className="truncate cm-display" style={{ fontWeight: 700 }}>{it.titre}</h3>
                    <p className="text-sm truncate" style={{ color: C.mut }}>{it.description || it.categorie}</p>
                    <div className="flex items-center justify-between pt-3 mt-3 border-t" style={{ borderColor: C.line }}>
                      <div className="flex items-center gap-2 text-xs" style={{ color: C.mut }}>
                        <Switch checked={isOn(it.active)} onChange={() => toggleActive(it)} label={isOn(it.active) ? 'Masquer de l’accueil' : 'Publier sur l’accueil'} />
                        {isOn(it.active) ? 'Visible' : 'Masquée'}
                      </div>
                      <div className="flex">
                        <button type="button" onClick={() => open('creations', it)} aria-label={`Modifier ${it.titre}`} className="p-2 rounded-lg cm-focus hover:bg-gray-100"><Pencil className="w-4 h-4" /></button>
                        <button type="button" onClick={() => setDel({ m: 'creations', item: it })} aria-label={`Supprimer ${it.titre}`} className="p-2 rounded-lg cm-focus hover:bg-red-50" style={{ color: '#C0392B' }}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="overflow-hidden bg-white rounded-2xl" style={{ border: `1px solid ${C.line}` }}>
            {visible.map((it, i) => {
              const st = statusOf(it);
              return (
                <div key={it.id} className="relative flex items-center gap-4 py-4 pl-6 pr-4 hover:bg-gray-50" style={{ borderTop: i ? `1px solid ${C.line}` : 'none', opacity: st === 'inactive' ? .75 : 1 }}>
                  <span className="absolute top-0 left-0 w-1 h-full" style={{ background: prioColor(it.priorite) }} />
                  {imgOf(it) && <img src={imgOf(it)} alt="" className="flex-none object-cover w-16 h-16 rounded-xl" />}
                  <button type="button" onClick={() => open('annonces', it)} className="flex-1 min-w-0 text-left cm-focus">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-xs font-bold rounded-md" style={{ background: C.bg, color: C.mut }}>{it.categorie}</span>
                      {it.priorite === 'urgent' && <span className="px-2 py-0.5 text-xs font-bold rounded-md" style={{ background: '#FFE9E6', color: '#B3321F' }}>Urgent</span>}
                      <StatusChip status={st} />
                    </div>
                    <div className="truncate cm-display" style={{ fontWeight: 700 }}>{it.titre}</div>
                    <div className="text-sm truncate" style={{ color: C.mut }}>{it.contenu}</div>
                    <div className="mt-1 text-xs" style={{ color: C.mut }}>
                      {it.date_publication ? `Publiée le ${fmtDate(it.date_publication)}` : ''}{it.date_expiration ? ` · expire le ${fmtDate(it.date_expiration)}` : ''}{it.auteur ? ` · ${it.auteur}` : ''}
                    </div>
                  </button>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => toggleActive(it)} aria-label={isOn(it.active) ? `Masquer ${it.titre}` : `Publier ${it.titre}`} title={isOn(it.active) ? 'Masquer' : 'Publier'} className="p-2 rounded-lg cm-focus hover:bg-gray-100">
                      {isOn(it.active) ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button type="button" onClick={() => open('annonces', it)} aria-label={`Modifier ${it.titre}`} className="p-2 rounded-lg cm-focus hover:bg-gray-100"><Pencil className="w-4 h-4" /></button>
                    <button type="button" onClick={() => setDel({ m: 'annonces', item: it })} aria-label={`Supprimer ${it.titre}`} className="p-2 rounded-lg cm-focus hover:bg-red-50" style={{ color: '#C0392B' }}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editor && <Editor {...editor} onClose={() => setEditor(null)} onSaved={onSaved} notify={notify} />}

      {del && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(11,20,55,.55)' }}>
          <div className="w-full max-w-sm p-6 bg-white shadow-2xl rounded-2xl" role="alertdialog" aria-label="Confirmer la suppression">
            <h3 className="text-lg cm-display" style={{ fontWeight: 800 }}>Supprimer « {del.item.titre} » ?</h3>
            <p className="mt-2 text-sm" style={{ color: C.mut }}>Cet élément disparaîtra de l’accueil. Pour le retirer temporairement, masquez-le plutôt.</p>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setDel(null)} className="px-4 py-2 text-sm font-bold border rounded-xl cm-focus" style={{ borderColor: C.line }}>Annuler</button>
              <button type="button" onClick={confirmDelete} className="px-4 py-2 text-sm font-bold text-white rounded-xl cm-focus" style={{ background: C.coral }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div key={toast.id} role="status" className="fixed z-[70] flex items-center gap-2 px-4 py-3 text-sm font-bold text-white -translate-x-1/2 shadow-xl cm-toast bottom-6 left-1/2 rounded-xl"
          style={{ background: toast.kind === 'error' ? '#B3321F' : C.navy }}>
          {toast.kind === 'error' ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" style={{ color: C.y }} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default Communication;